import { useFetchAccountDetails } from '@/containers/Accounts/api';
import { api } from '@/lib/api';
import { selectCurrentAccountId } from '@/lib/redux/app.selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import {
  BulkDeleteContactListsParams,
  BulkDeleteContactListsResponse,
  ContactList,
  FetchContactListDetailsParams,
  FetchContactListDetailsResponse,
  FetchContactListsParams,
  FetchContactListsResponse,
  MapContactListFormData,
  MapContactListResponse,
  SingleDeleteContactListResponse,
  UploadContactListFormData,
  UploadContactListResponse,
} from './types';
import { calculateRateplanMetrics } from './utils/rateplanCalculations';

const _uploadContactList = async (
  formdata: UploadContactListFormData
): Promise<AxiosResponse<UploadContactListResponse>> => {
  return api.post('phonelists/uploadlist', formdata);
};
const useUploadContactList = () => {
  return useMutation({
    mutationFn: _uploadContactList,
  });
};

const _mapContactList = async (
  formdata: MapContactListFormData
): Promise<AxiosResponse<MapContactListResponse>> => {
  return api.post('phonelists/maplist', formdata);
};
const useMapContactList = () => {
  return useMutation({
    mutationFn: _mapContactList,
  });
};

const _fetchContactLists = async ({
  accountNumber,
  usesRateplan,
}: FetchContactListsParams): Promise<AxiosResponse<FetchContactListsResponse>> => {
  const params = new URLSearchParams({
    account: accountNumber,
    include_sublists: 'true',
  });

  if (usesRateplan !== undefined) {
    params.append('uses_rateplan', usesRateplan ? '1' : '0');
  }

  return api.get(`phonelists/listByAccount?${params.toString()}`);
};
const useFetchContactLists = (
  accountNumber?: FetchContactListsParams['accountNumber'],
  enabled?: boolean,
  usesRateplan?: boolean
) => {
  return useQuery({
    queryKey: ['fetch-contact-lists', { accountNumber, usesRateplan }],
    queryFn: () => _fetchContactLists({ accountNumber: accountNumber!, usesRateplan }),
    enabled: !!accountNumber && (!!enabled || typeof enabled === 'undefined'),
    retry: 0,
    select: (data) => {
      return {
        ...data,
        data: data.data.map((contactList) => {
          // For the list page, we only have rateplan_adjusted, not the full breakdown
          // Just calculate the simple percentage for display
          if (contactList.rateplan_adjusted && contactList.leads_loaded > 0) {
            const rateplan_savings_total_count_perc =
              (contactList.rateplan_adjusted / contactList.leads_loaded) * 100;

            return {
              ...contactList,
              rateplan_savings_total_count_perc,
            };
          }

          return contactList;
        }),
      };
    },
  });
};

const _fetchPendingContactLists = async ({
  accountNumber,
  usesRateplan,
}: FetchContactListsParams): Promise<AxiosResponse<FetchContactListsResponse>> => {
  const params = new URLSearchParams({
    account: accountNumber,
  });

  if (usesRateplan !== undefined) {
    params.append('uses_rateplan', usesRateplan ? '1' : '0');
  }

  return api.get(`phonelists/pendinglistByAccount?${params.toString()}`);
};
const useFetchPendingContactLists = (
  accountNumber?: FetchContactListsParams['accountNumber'],
  enabled?: boolean,
  usesRateplan?: boolean
) => {
  return useQuery({
    queryKey: ['fetch-pending-contact-lists', { accountNumber, usesRateplan }],
    queryFn: () =>
      _fetchPendingContactLists({ accountNumber: accountNumber!, usesRateplan }),
    enabled: !!accountNumber && (!!enabled || typeof enabled === 'undefined'),
    retry: 0,
    refetchInterval: 10000,
    staleTime: 0, // Always refetch for polling
    select: (data) => {
      return {
        ...data,
        data: data.data.map((contactList) => {
          // For the list page, we only have rateplan_adjusted, not the full breakdown
          // Just calculate the simple percentage for display
          if (contactList.rateplan_adjusted && contactList.leads_loaded > 0) {
            const rateplan_savings_total_count_perc =
              (contactList.rateplan_adjusted / contactList.leads_loaded) * 100;

            return {
              ...contactList,
              rateplan_savings_total_count_perc,
            };
          }

          return contactList;
        }),
      };
    },
  });
};

// Delete functions
const _bulkDeleteContactLists = async (
  params: BulkDeleteContactListsParams
): Promise<AxiosResponse<BulkDeleteContactListsResponse>> => {
  return api.post('phonelists/bulkDelete', params);
};

const _singleDeleteContactList = async (
  listId: string
): Promise<AxiosResponse<SingleDeleteContactListResponse>> => {
  return api.delete(`phonelists/${listId}?force=true`);
};

const useBulkDeleteContactLists = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _bulkDeleteContactLists,
    onMutate: async (params: BulkDeleteContactListsParams) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['fetch-contact-lists'] });

      // Snapshot the previous value
      const previousQueries = queryClient.getQueriesData({
        queryKey: ['fetch-contact-lists'],
      });

      // Optimistically remove the contact lists from all relevant queries
      queryClient.setQueriesData(
        { queryKey: ['fetch-contact-lists'] },
        (old: unknown) => {
          if (!old || typeof old !== 'object') return old;

          const queryData = old as AxiosResponse<ContactList[]>;

          return {
            ...queryData,
            data: queryData.data.filter(
              (list: ContactList) => !params.list_ids.includes(list.list_id.toString())
            ),
          };
        }
      );

      // Return a context object with the snapshotted value
      return { previousQueries };
    },
    onError: (error, params, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ['fetch-contact-lists'],
        exact: false,
      });
    },
  });
};

const useSingleDeleteContactList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _singleDeleteContactList,
    onMutate: async (listId: string) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['fetch-contact-lists'] });

      // Snapshot the previous value
      const previousQueries = queryClient.getQueriesData({
        queryKey: ['fetch-contact-lists'],
      });

      // Optimistically remove the contact list from all relevant queries
      queryClient.setQueriesData(
        { queryKey: ['fetch-contact-lists'] },
        (old: unknown) => {
          if (!old || typeof old !== 'object') return old;

          const queryData = old as AxiosResponse<ContactList[]>;

          return {
            ...queryData,
            data: queryData.data.filter(
              (list: ContactList) => list.list_id.toString() !== listId
            ),
          };
        }
      );

      // Return a context object with the snapshotted value
      return { previousQueries };
    },
    onError: (error, listId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ['fetch-contact-lists'],
        exact: false,
      });
    },
  });
};

// Fetch contact list details
const _fetchContactListDetails = async ({
  listId,
}: FetchContactListDetailsParams): Promise<
  AxiosResponse<FetchContactListDetailsResponse>
> => {
  return api.get(`phonelists/${listId}?include_sublists=true`);
};

const useFetchContactListDetails = (
  listId?: FetchContactListDetailsParams['listId'],
  enabled?: boolean
) => {
  const currentAccountId = useSelector(selectCurrentAccountId);
  const { data: accountDetails } = useFetchAccountDetails(
    currentAccountId || undefined,
    !!currentAccountId
  );

  const p2pRateplan = accountDetails?.data?.p2p_rateplan;

  return useQuery({
    queryKey: ['fetch-contact-list-details', { listId, hasRateplan: !!p2pRateplan }],
    queryFn: () => _fetchContactListDetails({ listId: listId! }),
    enabled: !!listId && (!!enabled || typeof enabled === 'undefined'),
    retry: 0,
    select: (data) => {
      // Transform the data to include calculated rateplan metrics
      if (!p2pRateplan) {
        return data;
      }

      const contactListDetails = data.data;

      // Only calculate if the contact list has rateplan data from the API
      if (contactListDetails.rateplan_adjusted && contactListDetails.rateplan_breakdown) {
        const calculatedMetrics = calculateRateplanMetrics({
          rateplan_breakdown: contactListDetails.rateplan_breakdown,
          rateplan_adjusted: contactListDetails.rateplan_adjusted,
          leads_loaded: contactListDetails.leads_loaded,
          p2p_rateplan: p2pRateplan,
        });

        return {
          ...data,
          data: {
            ...contactListDetails,
            ...calculatedMetrics,
          },
        };
      }

      return data;
    },
  });
};

export {
  useBulkDeleteContactLists,
  useFetchContactListDetails,
  useFetchContactLists,
  useFetchPendingContactLists,
  useMapContactList,
  useSingleDeleteContactList,
  useUploadContactList,
};
